import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';
import RealmSafeSeatsioChart from '../../WebOrderForm/components/RealmSafeSeatsioChart';
import axios from 'axios';
import { Toggle } from '@opendate-app/setlist';
import { holdObjectColor, holdObjectIcon, openEveryNightVisible, determineChannelKey, getHoldPopoverInfo, allInPricePopoverInfo } from '../../../shared/seatsioUtils';
import { channelsForEvent } from '../../../shared/channelsByEvent';
import usePageColorScheme from '../../shared/hooks/usePageColorScheme';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const AddOnSeatingChart = ({
  team,
  csrfToken,
  confirm,
  ticketTypes,
  addOns,
  visibleAddOns: originalVisibleAddOns,
  addOnsToSell: originalAddOnsToSell,
  promoCode,
  addOnsChanged,
  ticketReservation,
  postToParent,
  formatItemsForGoogleTagManager,
  seatsIOPublicKey,
  selectedObjectLabel,
  selectedObjectType,
  buildTicketReservation,
  sharedInventoryMode,
  userContext,
  showDescription,
  displayChartText,
  displayChartButtonClass,
  checkoutGroupItem,
  eventHolds,
  startAtAddOns,
  bypassTicketRequirement,
  adminPreview
}) => {
  const colorScheme = usePageColorScheme();
  const [addOnSeatsIOChart, addOnSeatsIOChartChanged] = useState({});
  const [seatsSelected, setSeatsSelected] = useState([]);
  const [onlyEveryNight, setOnlyEveryNight] = useState(false);
  const [blockedLabels, setBlockedLabels] = useState([]);
  const [chartVersion, setChartVersion] = useState(0);
  const [displayChart, setDisplayChart] = useState(!sharedInventoryMode);

  // Stable placeholder category for the "no visible categories" case. Regenerating
  // it every render changes the chart's availableCategories prop each render, which
  // re-inits the seats.io chart in an infinite loop (a request storm against
  // seatsio.net that freezes the tab). See chartAvailableCategories below.
  const [mockCategoryId] = useState(() => uuidv4());

  const [seatsioEventId, setSeatsioEventId] = useState(
    Object.keys(checkoutGroupItem).length > 0 ? checkoutGroupItem.extra.seatsio_event_id : confirm.add_on_seatsio_event_id
  );

  const [seatingChartEventId, setSeatingChartEventId] = useState(
    Object.keys(checkoutGroupItem).length > 0 ? checkoutGroupItem.item_id : confirm.add_on_seating_chart_event_id
  );

  const showEveryNightToggle = !adminPreview && !!checkoutGroupItem?.extra?.has_other_nights;

  const onlyEveryNightChanged = async (checked) => {
    setOnlyEveryNight(checked);
    try {
      const labels = checked
        ? (await axios.get(`/confirms/${confirm.id}/web_orders/seats_taken_on_other_nights`, { params: { seating_chart_event_id: seatingChartEventId } })).data.labels
        : [];
      addOnSeatsIOChart.clearSelection?.();
      setSeatsSelected([]);
      setBlockedLabels(labels);
      setChartVersion((version) => version + 1);
    } catch (error) {
      setOnlyEveryNight(false);
    }
  };

  const [addOnsToSell, setAddOnsToSell] = useState(
    originalAddOnsToSell.filter(addOn =>
      (addOn.seating_chart_event_id === seatingChartEventId)
    )
  );

  useEffect(() => {
    setAddOnsToSell(
      originalAddOnsToSell.filter(addOn =>
        (addOn.seating_chart_event_id === seatingChartEventId)
      )
    )
  }, [originalAddOnsToSell]);

  const primaryAddOn = (sharedInventoryMode && addOnsToSell.length === 1 ? addOnsToSell[0] : null);

  const [visibleAddOns, setVisibleAddOns] = useState(
    originalVisibleAddOns.filter(addOn =>
      (addOn.seating_chart_event_id === seatingChartEventId)
    )
  );

  useEffect(() => {
    setVisibleAddOns(
      originalVisibleAddOns.filter(addOn =>
        (addOn.seating_chart_event_id === seatingChartEventId)
      )
    )
  }, [originalVisibleAddOns]);

  const eventHoldChannels = eventHolds?.allChannelKeysIncludingNoChannel;
  const configuredChannels = (eventHoldChannels && eventHoldChannels.length > 0)
    ? eventHoldChannels
    : (channelsForEvent(seatsioEventId) || []);

  // Mirrors AddOnOrderItem#overrides_ticket_requirements on the backend:
  // direct-add-on-link entry point bypasses the "qualifying tickets in cart" check.
  const getEligibleTicketCount = (addOn) => {
    const overridesTicketRequirement =
      addOn.disable_require_ticket_type_with_direct_link && startAtAddOns;

    if (!addOn.require_ticket_type || overridesTicketRequirement || bypassTicketRequirement) {
      return addOn.maximum_per_order;
    }

    const purchasingCount = ticketTypes
      .filter(tt => (addOn.ticket_type_ids || []).includes(tt.id))
      .reduce((sum, tt) => sum + (tt.quantity || 0), 0);

    const ownedCount = (userContext?.tickets || [])
      .filter(t => (addOn.ticket_type_ids || []).includes(t.ticket_type_id))
      .length;

    const qualifyingCount = purchasingCount + ownedCount;
    return Math.min(addOn.maximum_per_order, qualifyingCount);
  };

  const seatsIOObjectSelected = (object) => {
    setSeatsSelected((prevSeatsSelected) => {
      var updatedAddOn = addOns.find((addOn) =>
        addOn.seating_chart_category_key === object.category.key.toString()
      );

      var items = formatItemsForGoogleTagManager(
        confirm, promoCode, [], [updatedAddOn]
      );

      postToParent("ga.add_to_cart", items)
      return [...prevSeatsSelected, object];
    });
  };

  const seatsIOObjectDeselected = (object) => {
    setSeatsSelected((prevSeatsSelected) => {
      return prevSeatsSelected.filter((o) =>
        o.id !== object.id
      );
    });
  };

  useEffect(() => {
    var addOnIdsToSell = addOnsToSell.map((ao) => ao.id);

    var updatedAddOns = [...addOns].map((ao) => {
      if(!ao.seating_chart_category_key){
        return ao;
      }

      if(!addOnIdsToSell.includes(ao.id)){
        return ao;
      }

      var filteredObjects = seatsSelected.filter((o) =>
        o.category.key.toString() === ao.seating_chart_category_key
      );

      return Object.assign({}, ao, {
        seatsIOObjects: filteredObjects.map((object) => {
          return {
            seating_chart_event_id: seatingChartEventId,
            seatsio_object_id: object.id,
            seat_assignment: selectedObjectLabel(object),
            seat_type: selectedObjectType(object),
            hold_token: addOnSeatsIOChart.holdToken,
            channel_key: determineChannelKey(object, configuredChannels)
          }
        }),
        quantity: filteredObjects.length
      });
    });

    addOnsChanged(updatedAddOns);
    buildTicketReservation(updatedAddOns);
  }, [seatsSelected]);

  const chartAvailableCategories = (addOnsToSell, visibleAddOns) => {
    var categoryIdsToSell = addOnsToSell
      .map((ao) => ao.seating_chart_category_key);

    var visibleCategoryIds = visibleAddOns
      .filter((ao) => categoryIdsToSell.includes(ao.seating_chart_category_key))
      .map((ao) => ao.seating_chart_category_key);

    // Empty array means everything is available
    if(visibleCategoryIds.length === 0){
      visibleCategoryIds.push(mockCategoryId);
    }

    return visibleCategoryIds;
  }

  const chartPricing = (addOnsToSell) => {
    return addOnsToSell.map((ao) => {
      return {
        category: ao.seating_chart_category_key,
        price: parseFloat(ao.calculated_price)
      };
    })
  }

  useEffect(() => {
    if(addOnSeatsIOChart && addOnSeatsIOChart.holdToken){
      addOnSeatsIOChart.changeConfig({
        pricing: chartPricing(addOnsToSell)
      });
    }
  }, [promoCode]);

  useEffect(() => {
    if (addOnSeatsIOChart && addOnSeatsIOChart.holdToken) {
      addOnSeatsIOChart.changeConfig({
        maxSelectedObjects: addOnsToSell.map((ao) => ({
          category: ao.seating_chart_category_key,
          quantity: getEligibleTicketCount(ao)
        }))
      });
    }
  }, [ticketTypes, startAtAddOns]);

  return (
    <div>
      <div className="card border-0"
            style={{"background": "var(--od-c-f9f9f9, #f9f9f9)", "marginBottom": "15px"}}>
        <div className="card-body"
            style={{
              "paddingLeft": "15px",
              "paddingRight": "15px"
            }}>
          <div className="row">
            <div className="col">
              {primaryAddOn ? (
                <>
                  <p className="mb-0"
                    style={{"fontSize": "16px"}}>
                    <strong>{primaryAddOn.name}</strong>
                  </p>
                  <p className="mb-0" style={{"fontSize": "16px"}}>
                    {formatter.format(parseFloat(primaryAddOn.calculated_price))}
                  </p>
                  {showDescription && primaryAddOn.description && primaryAddOn.description.length > 0 ? (
                    <div className="mb-0 mt-2 small last-p-margin-0"
                          dangerouslySetInnerHTML={{__html: primaryAddOn.simple_format_description}}
                          style={{"maxWidth": "400px"}}>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
            {sharedInventoryMode ? (
              (primaryAddOn.available_tickets == 0 || primaryAddOn.sold_out) ? (
                <div className="col d-flex align-items-center justify-content-end">
                  <p className="mb-0"
                    style={{"fontSize": "14px", "color": "var(--od-c-686868, #686868)"}}>
                    Sold out
                  </p>
                </div>
              ) : (
                <div className='col-xs-auto'
                      style={{"paddingRight": "15px"}}>
                  {displayChart ? (
                    <a href="#"
                        onClick={
                          (e) => {
                            e.preventDefault();
                            var requestPromises = [];

                            // In theory clearSelection should be enough, but there is some sort
                            // of order of operations problem here where the buildTicketReservation
                            // request still thinks there are items selected when it gets sent.
                            if(seatsSelected.length > 0){
                              setSeatsSelected([]);
                            }

                            var shouldDeselect = (
                              addOnSeatsIOChart
                                && addOnSeatsIOChart.clearSelection
                                && (
                                  addOnSeatsIOChart.selectedObjects.length > 0
                                    || addOnSeatsIOChart.selectedSeats.length > 0
                                )
                            );

                            if (shouldDeselect){
                              requestPromises.push(
                                addOnSeatsIOChart.clearSelection()
                              );
                            }

                            Promise.all(requestPromises)
                              .then(() => {
                                addOnSeatsIOChart.destroy();
                                setDisplayChart(!displayChart);
                              });
                          }
                        }
                        style={{
                          "fontSize": "14px",
                          "borderWidth": "1px",
                          "borderRadius": "4px"
                        }}
                        className={displayChartButtonClass}>
                      <strong>Deselect</strong>
                    </a>
                  ) : (
                    <a href="#"
                        onClick={
                          (e) => {
                            e.preventDefault();
                            setDisplayChart(!displayChart);
                          }
                        }
                        style={{
                          "fontSize": "14px",
                          "borderWidth": "1px",
                          "borderRadius": "4px"
                        }}
                        className={displayChartButtonClass}>
                      <strong>{displayChartText}</strong>
                    </a>
                  )}
                </div>
              )
            ) : null}
          </div>
          <div className="row">
            <div className="col-12">
              {displayChart && showEveryNightToggle ? (
                <div className="mt-3">
                  <Toggle
                    label="Only show seats open every night"
                    description="Hide seats already taken on another night of this show."
                    checked={onlyEveryNight}
                    onChange={(event) => onlyEveryNightChanged(event.target.checked)}
                  />
                </div>
              ) : null}
              {displayChart ? (
                <div className="card mb-0 mt-3"
                    style={{
                      "border": "1px solid var(--od-c-f3f3f3, #f3f3f3)",
                      "height": "515px"
                    }}>
                  {seatsioEventId && seatsioEventId.length > 0 ? (
                    <RealmSafeSeatsioChart workspaceKey={seatsIOPublicKey}
                                        event={seatsioEventId}
                                        availableCategories={chartAvailableCategories(addOnsToSell, visibleAddOns)}
                                        fitTo={'widthAndHeight'}
                                        mode={adminPreview ? 'static' : undefined}
                                        session={adminPreview ? 'none' : undefined}
                                        maxSelectedObjects={
                                          addOnsToSell.map((ao) => ({
                                            category: ao.seating_chart_category_key,
                                            quantity: getEligibleTicketCount(ao)
                                          }))
                                        }
                                        onRenderStarted={
                                          chart => {
                                            addOnSeatsIOChartChanged(chart);

                                            chart.changeConfig({
                                              pricing: chartPricing(addOnsToSell)
                                            });

                                            if(!adminPreview){
                                              chart.config.session = "start";
                                              chart.config.onObjectSelected = (object) => seatsIOObjectSelected(object);
                                              chart.config.onObjectDeselected = (object) => seatsIOObjectDeselected(object);
                                            }
                                          }
                                        }
                                        popoverInfo={
                                          (object) => {
                                            const holdInfo = getHoldPopoverInfo(object, eventHolds);
                                            if (holdInfo) {
                                              return holdInfo;
                                            }

                                            return allInPricePopoverInfo(object, confirm, addOns);
                                          }
                                        }
                                        priceFormatter={
                                          price => formatter.format(price)
                                        }
                                        channels={configuredChannels}
                                        key={chartVersion}
                                        extraConfig={{
                                          labelToHoldConfig: eventHolds?.labelToHoldConfig,
                                          blockedLabels
                                        }}
                                        objectColor={holdObjectColor}
                                        isObjectVisible={openEveryNightVisible}
                                        objectIcon={holdObjectIcon}
                                        colorScheme={colorScheme}
                                        region="na" />
                  ) : (
                    <div className="row">
                      <div className="col-12">
                        <div className="card border-0">
                          <div className="card-body d-flex align-items-center justify-content-center">
                            <div className="text-center">
                              <p className="mb-2"
                                style={{"fontSize": "16px"}}>
                                <strong>An error occurred.</strong>
                              </p>
                              <p className="mb-0">
                                Please try again later.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

AddOnSeatingChart.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  addOns: PropTypes.array.isRequired,
  visibleAddOns: PropTypes.array,
  promoCode: PropTypes.object,
  addOnsChanged: PropTypes.func.isRequired,
  ticketReservation: PropTypes.object,
  postToParent: PropTypes.func.isRequired,
  formatItemsForGoogleTagManager: PropTypes.func.isRequired,
  seatsIOPublicKey: PropTypes.string.isRequired,
  selectedObjectLabel: PropTypes.func.isRequired,
  selectedObjectType: PropTypes.func.isRequired,
  buildTicketReservation: PropTypes.func.isRequired,
  addOnsToSell: PropTypes.array.isRequired,
  sharedInventoryMode: PropTypes.bool.isRequired,
  userContext: PropTypes.object,
  showDescription: PropTypes.bool,
  displayChartText: PropTypes.string,
  displayChartButtonClass: PropTypes.string,
  checkoutGroupItem: PropTypes.object,
  eventHolds: PropTypes.object,
  startAtAddOns: PropTypes.bool,
  bypassTicketRequirement: PropTypes.bool,
  adminPreview: PropTypes.bool
};

AddOnSeatingChart.defaultProps = {
  showDescription: true,
  displayChartText: "Choose your seat",
  displayChartButtonClass: "btn btn-outline-danger",
  checkoutGroupItem: {},
  startAtAddOns: false,
  bypassTicketRequirement: false
};

export default AddOnSeatingChart;
